/**
 * RewardConfigEditor Component
 *
 * Editor for RewardConfig JSONB with form/JSON toggle.
 * Sections: Limits (monthly only), Yearly Budget, Monthly Budget, Availability, Completion Points.
 *
 * Annual limits live exclusively on the annual_pool ("Yearly Budget" in the UI).
 * Monthly pool limits live on the monthly_pool ("Monthly Budget" in the UI).
 * The word "pool" never appears in the admin-facing UI.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Input, Select, Checkbox } from '@/components/common/FormField';
import { rewardService } from '@/app/services/rewardService';
import type { RewardConfig, LimitType, LimitPoolConfig, PoolInfo } from '@/app/types/rewards';
import { LIMIT_TYPE_OPTIONS } from '@/app/types/rewards';

import './RewardConfigEditor.scss';

interface RewardConfigEditorProps {
  value: RewardConfig | null;
  onChange: (value: RewardConfig | null) => void;
}

type EditorMode = 'structured' | 'json';

const DEFAULT_CONFIG: RewardConfig = {
  limit_type: 'unlimited',
  available: true,
};

function generatePoolId(limitType: string, limitValue: number, period: 'yearly' | 'monthly'): string {
  const type = limitType === 'count' ? 'count' : 'dollar';
  const value = limitType === 'count' ? Math.floor(limitValue) : limitValue;
  return `${type}-${value}-${period}`;
}

function formatBudgetLabel(pool: PoolInfo, limitType: LimitType): string {
  if (pool.limit_count != null && limitType === 'count') {
    return `${pool.limit_count.toLocaleString()} redemptions (${pool.pool_id})`;
  }
  if (pool.limit_usd != null) {
    return `$${pool.limit_usd.toLocaleString()} (${pool.pool_id})`;
  }
  return `No limit set (${pool.pool_id})`;
}

function formatLimitDisplay(pool: LimitPoolConfig | undefined | null, limitType: LimitType): string {
  if (!pool) return 'Not set';
  if (limitType === 'count' && pool.limit_count != null) {
    return `${pool.limit_count.toLocaleString()} redemptions`;
  }
  if (pool.limit_usd != null) {
    return `$${pool.limit_usd.toLocaleString()}`;
  }
  return 'Not set';
}

export const RewardConfigEditor: React.FC<RewardConfigEditorProps> = ({
  value,
  onChange,
}) => {
  const [mode, setMode] = useState<EditorMode>('structured');
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [availableAnnualPools, setAvailableAnnualPools] = useState<PoolInfo[]>([]);
  const [availableMonthlyPools, setAvailableMonthlyPools] = useState<PoolInfo[]>([]);
  const [isCreatingAnnualBudget, setIsCreatingAnnualBudget] = useState(false);
  const [newAnnualBudgetAmount, setNewAnnualBudgetAmount] = useState('');
  const [isCreatingMonthlyBudget, setIsCreatingMonthlyBudget] = useState(false);
  const [newMonthlyBudgetAmount, setNewMonthlyBudgetAmount] = useState('');

  useEffect(() => {
    rewardService.getPools().then((response) => {
      setAvailableAnnualPools(response.annual_pools);
      setAvailableMonthlyPools(response.monthly_pools);
    });
  }, []);

  useEffect(() => {
    if (mode === 'json') {
      setJsonText(value ? JSON.stringify(value, null, 2) : '');
    }
  }, [value, mode]);

  const handleModeChange = (newMode: EditorMode) => {
    if (newMode === 'json') {
      setJsonText(value ? JSON.stringify(value, null, 2) : '');
      setJsonError(null);
    }
    setMode(newMode);
  };

  const handleJsonChange = (text: string) => {
    setJsonText(text);
    if (!text.trim()) {
      setJsonError(null);
      onChange(null);
      return;
    }
    try {
      const parsed = JSON.parse(text);
      setJsonError(null);
      onChange(parsed as RewardConfig);
    } catch {
      setJsonError('Invalid JSON');
    }
  };

  const updateField = useCallback(
    <K extends keyof RewardConfig>(key: K, fieldValue: RewardConfig[K]) => {
      const updated = { ...(value || DEFAULT_CONFIG), [key]: fieldValue };
      onChange(updated);
    },
    [value, onChange]
  );

  const updateLimitType = useCallback(
    (limitType: LimitType) => {
      const updated: RewardConfig = {
        ...(value || DEFAULT_CONFIG),
        limit_type: limitType,
      };
      if (limitType === 'unlimited') {
        delete updated.monthly_limit_usd;
        delete updated.annual_limit_usd;
        delete updated.monthly_limit_count;
        delete updated.annual_limit_count;
      } else if (limitType === 'dollar') {
        delete updated.monthly_limit_count;
        delete updated.annual_limit_count;
      } else if (limitType === 'count') {
        delete updated.monthly_limit_usd;
        delete updated.annual_limit_usd;
      }
      onChange(updated);
    },
    [value, onChange]
  );

  // --- Annual budget handlers ---
  const handleAnnualBudgetSelect = useCallback(
    (selectedValue: string) => {
      if (!selectedValue) return;
      if (selectedValue === '__new__') {
        setIsCreatingAnnualBudget(true);
        setNewAnnualBudgetAmount('');
        return;
      }
      if (selectedValue === '__remove__') {
        const updated = { ...(value || DEFAULT_CONFIG) };
        delete updated.annual_pool;
        onChange(updated);
        setIsCreatingAnnualBudget(false);
        setNewAnnualBudgetAmount('');
        return;
      }
      const pool = availableAnnualPools.find((p) => p.pool_id === selectedValue);
      if (pool) {
        const poolConfig: LimitPoolConfig = {
          pool_id: pool.pool_id,
          limit_usd: pool.limit_usd,
          limit_count: pool.limit_count,
        };
        const updated = { ...(value || DEFAULT_CONFIG), annual_pool: poolConfig };
        delete updated.annual_limit_usd;
        delete updated.annual_limit_count;
        onChange(updated);
        setIsCreatingAnnualBudget(false);
      }
    },
    [value, onChange, availableAnnualPools]
  );

  const handleCreateAnnualBudget = useCallback(() => {
    const limit = newAnnualBudgetAmount ? parseFloat(newAnnualBudgetAmount) : null;
    if (limit == null || limit <= 0) return;
    const currentLimitType = value?.limit_type || 'unlimited';
    const poolConfig: LimitPoolConfig = {
      pool_id: generatePoolId(currentLimitType, limit, 'yearly'),
      limit_usd: currentLimitType === 'count' ? null : limit,
      limit_count: currentLimitType === 'count' ? Math.floor(limit) : null,
    };
    const updated = { ...(value || DEFAULT_CONFIG), annual_pool: poolConfig };
    delete updated.annual_limit_usd;
    delete updated.annual_limit_count;
    onChange(updated);
    setIsCreatingAnnualBudget(false);
    setNewAnnualBudgetAmount('');
  }, [value, onChange, newAnnualBudgetAmount]);

  const handleRemoveAnnualBudget = useCallback(() => {
    const updated = { ...(value || DEFAULT_CONFIG) };
    delete updated.annual_pool;
    onChange(updated);
    setIsCreatingAnnualBudget(false);
    setNewAnnualBudgetAmount('');
  }, [value, onChange]);

  // --- Monthly budget handlers ---
  const handleMonthlyBudgetSelect = useCallback(
    (selectedValue: string) => {
      if (!selectedValue) return;
      if (selectedValue === '__new__') {
        setIsCreatingMonthlyBudget(true);
        setNewMonthlyBudgetAmount('');
        return;
      }
      if (selectedValue === '__remove__') {
        const updated = { ...(value || DEFAULT_CONFIG) };
        delete updated.monthly_pool;
        onChange(updated);
        setIsCreatingMonthlyBudget(false);
        setNewMonthlyBudgetAmount('');
        return;
      }
      const pool = availableMonthlyPools.find((p) => p.pool_id === selectedValue);
      if (pool) {
        const poolConfig: LimitPoolConfig = {
          pool_id: pool.pool_id,
          limit_usd: pool.limit_usd,
          limit_count: pool.limit_count,
        };
        const updated = { ...(value || DEFAULT_CONFIG), monthly_pool: poolConfig };
        onChange(updated);
        setIsCreatingMonthlyBudget(false);
      }
    },
    [value, onChange, availableMonthlyPools]
  );

  const handleCreateMonthlyBudget = useCallback(() => {
    const limit = newMonthlyBudgetAmount ? parseFloat(newMonthlyBudgetAmount) : null;
    if (limit == null || limit <= 0) return;
    const currentLimitType = value?.limit_type || 'unlimited';
    const poolConfig: LimitPoolConfig = {
      pool_id: generatePoolId(currentLimitType, limit, 'monthly'),
      limit_usd: currentLimitType === 'count' ? null : limit,
      limit_count: currentLimitType === 'count' ? Math.floor(limit) : null,
    };
    const updated = { ...(value || DEFAULT_CONFIG), monthly_pool: poolConfig };
    onChange(updated);
    setIsCreatingMonthlyBudget(false);
    setNewMonthlyBudgetAmount('');
  }, [value, onChange, newMonthlyBudgetAmount]);

  const handleRemoveMonthlyBudget = useCallback(() => {
    const updated = { ...(value || DEFAULT_CONFIG) };
    delete updated.monthly_pool;
    onChange(updated);
    setIsCreatingMonthlyBudget(false);
    setNewMonthlyBudgetAmount('');
  }, [value, onChange]);

  const handleClear = () => {
    onChange(null);
    setJsonText('');
    setJsonError(null);
    setIsCreatingAnnualBudget(false);
    setNewAnnualBudgetAmount('');
    setIsCreatingMonthlyBudget(false);
    setNewMonthlyBudgetAmount('');
  };

  const initializeConfig = () => {
    onChange({ ...DEFAULT_CONFIG });
  };

  const hasAnnualPool = !!value?.annual_pool?.pool_id;
  const hasMonthlyPool = !!value?.monthly_pool?.pool_id;

  const limitTypeOptions = LIMIT_TYPE_OPTIONS.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));

  const currentLimitType = value?.limit_type || 'unlimited';

  // --- Annual budget select options ---
  const compatibleAnnualPools = availableAnnualPools.filter(
    (p) => p.limit_type === currentLimitType
  );

  const annualBudgetSelectOptions = [
    { value: '', label: 'No yearly limit' },
    ...compatibleAnnualPools.map((p) => ({
      value: p.pool_id,
      label: formatBudgetLabel(p, currentLimitType),
    })),
    { value: '__new__', label: '+ Create new yearly budget' },
  ];

  if (hasAnnualPool) {
    const currentInList = compatibleAnnualPools.some(
      (p) => p.pool_id === value?.annual_pool?.pool_id
    );
    if (!currentInList) {
      const currentLabel = `${formatLimitDisplay(value?.annual_pool, currentLimitType)}/year (this reward)`;
      annualBudgetSelectOptions.splice(1, 0, {
        value: value!.annual_pool!.pool_id,
        label: currentLabel,
      });
    }
  }

  // --- Monthly budget select options ---
  const compatibleMonthlyPools = availableMonthlyPools.filter(
    (p) => p.limit_type === currentLimitType
  );

  const monthlyBudgetSelectOptions = [
    { value: '', label: 'No monthly shared limit' },
    ...compatibleMonthlyPools.map((p) => ({
      value: p.pool_id,
      label: formatBudgetLabel(p, currentLimitType),
    })),
    { value: '__new__', label: '+ Create new monthly budget' },
  ];

  if (hasMonthlyPool) {
    const currentInList = compatibleMonthlyPools.some(
      (p) => p.pool_id === value?.monthly_pool?.pool_id
    );
    if (!currentInList) {
      const currentLabel = `${formatLimitDisplay(value?.monthly_pool, currentLimitType)}/month (this reward)`;
      monthlyBudgetSelectOptions.splice(1, 0, {
        value: value!.monthly_pool!.pool_id,
        label: currentLabel,
      });
    }
  }

  return (
    <div className="reward-config-editor">
      {/* Mode Toggle */}
      <div className="mode-toggle">
        <button
          type="button"
          className={`toggle-btn ${mode === 'structured' ? 'active' : ''}`}
          onClick={() => handleModeChange('structured')}
        >
          Structured Form
        </button>
        <button
          type="button"
          className={`toggle-btn ${mode === 'json' ? 'active' : ''}`}
          onClick={() => handleModeChange('json')}
        >
          JSON Editor
        </button>
      </div>

      {mode === 'json' ? (
        <div className="json-editor">
          <textarea
            className={`json-textarea ${jsonError ? 'error' : ''}`}
            value={jsonText}
            onChange={(e) => handleJsonChange(e.target.value)}
            placeholder='{"limit_type": "unlimited", "available": true}'
            spellCheck={false}
          />
          {jsonError && <div className="json-error">{jsonError}</div>}
        </div>
      ) : (
        <div className="structured-editor">
          {!value ? (
            <div className="empty-state">
              <p>No reward configuration set.</p>
              <button
                type="button"
                className="btn-initialize"
                onClick={initializeConfig}
              >
                + Configure Reward
              </button>
            </div>
          ) : (
            <>
              {/* Limits Section -- monthly only */}
              <div className="form-section">
                <h4>Limits</h4>
                <div className="form-grid">
                  <Select
                    label="Limit Type"
                    value={value.limit_type}
                    onChange={(val) => updateLimitType(val as LimitType)}
                    options={limitTypeOptions}
                    hint="How redemptions are capped for this reward"
                  />
                </div>

                {value.limit_type === 'dollar' && (
                  <div className="form-grid" style={{ marginTop: '1rem' }}>
                    <Input
                      label="Monthly Limit (USD)"
                      type="number"
                      value={value.monthly_limit_usd?.toString() || ''}
                      onChange={(val) =>
                        updateField('monthly_limit_usd', val ? parseFloat(val) : null)
                      }
                      placeholder="No monthly limit"
                      hint="Max dollars per month for this reward"
                    />
                  </div>
                )}

                {value.limit_type === 'count' && (
                  <div className="form-grid" style={{ marginTop: '1rem' }}>
                    <Input
                      label="Monthly Limit (Count)"
                      type="number"
                      value={value.monthly_limit_count?.toString() || ''}
                      onChange={(val) =>
                        updateField('monthly_limit_count', val ? parseInt(val, 10) : null)
                      }
                      placeholder="No monthly limit"
                      hint="Max redemptions per month for this reward"
                    />
                  </div>
                )}
              </div>

              {/* Yearly Budget Section */}
              <div className="form-section">
                <h4>Yearly Budget</h4>
                <p className="section-hint">
                  Set a yearly spending limit. You can share the same yearly budget across
                  multiple rewards.
                </p>

                {!isCreatingAnnualBudget && (
                  <div className="form-grid">
                    <Select
                      label="Yearly Budget"
                      value={hasAnnualPool ? (value.annual_pool?.pool_id || '') : ''}
                      onChange={handleAnnualBudgetSelect}
                      options={annualBudgetSelectOptions}
                      hint="Pick an existing yearly budget or create a new one"
                    />
                  </div>
                )}

                {/* Create new annual budget form */}
                {isCreatingAnnualBudget && (
                  <div className="new-pool-card">
                    <h5 className="pool-card-title">Create a New Yearly Budget</h5>
                    <p className="pool-card-desc">
                      {value.limit_type === 'count'
                        ? 'Set the maximum number of redemptions per year. If you assign other rewards to this same budget later, they will all share it.'
                        : 'Set the maximum amount a user can spend per year. If you assign other rewards to this same budget later, they will all share it.'}
                    </p>
                    <div className="new-pool-fields">
                      <Input
                        label={value.limit_type === 'count' ? 'Yearly Limit (Count)' : 'Yearly Limit (USD)'}
                        type="number"
                        value={newAnnualBudgetAmount}
                        onChange={setNewAnnualBudgetAmount}
                        placeholder={value.limit_type === 'count' ? 'e.g., 12' : 'e.g., 600'}
                        hint={value.limit_type === 'count'
                          ? 'Maximum redemptions a user can make per year'
                          : 'Maximum dollars a user can spend per year'}
                      />
                    </div>
                    {newAnnualBudgetAmount && parseFloat(newAnnualBudgetAmount) > 0 && (
                      <div className="new-pool-preview">
                        {value.limit_type === 'count'
                          ? <>Users can redeem up to <strong>{parseInt(newAnnualBudgetAmount, 10).toLocaleString()} times</strong> per year.</>
                          : <>Users can spend up to <strong>${parseFloat(newAnnualBudgetAmount).toLocaleString()}</strong> per year.</>}
                      </div>
                    )}
                    <div className="pool-actions">
                      <button
                        type="button"
                        className="btn-create-pool"
                        onClick={handleCreateAnnualBudget}
                        disabled={!newAnnualBudgetAmount || parseFloat(newAnnualBudgetAmount) <= 0}
                      >
                        Set Yearly Budget
                      </button>
                      <button
                        type="button"
                        className="btn-back-to-existing"
                        onClick={() => setIsCreatingAnnualBudget(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Assigned annual budget info card */}
                {hasAnnualPool && !isCreatingAnnualBudget && (
                  <div className="pool-info-card" style={{ marginTop: '1rem' }}>
                    <div className="pool-info-row">
                      <span className="pool-info-label">Yearly Limit</span>
                      <span className="pool-info-value">
                        {formatLimitDisplay(value.annual_pool, currentLimitType)}/year
                      </span>
                    </div>
                    {(() => {
                      const matchingPool = availableAnnualPools.find(
                        (p) => p.pool_id === value?.annual_pool?.pool_id
                      );
                      if (matchingPool && matchingPool.members.length > 0) {
                        return (
                          <div className="pool-info-row">
                            <span className="pool-info-label">Shared with</span>
                            <span className="pool-info-value pool-members">
                              {matchingPool.members.map((m) => (
                                <span key={m.reward_id} className="pool-member-tag">
                                  {m.reward_key}/{m.reward_variant}
                                </span>
                              ))}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    <button
                      type="button"
                      className="btn-remove-pool"
                      onClick={handleRemoveAnnualBudget}
                    >
                      Remove yearly budget
                    </button>
                  </div>
                )}
              </div>

              {/* Monthly Budget Section */}
              <div className="form-section">
                <h4>Monthly Budget</h4>
                <p className="section-hint">
                  Set a monthly spending limit shared across reward variants. When users switch
                  variants, their monthly usage carries over.
                </p>

                {!isCreatingMonthlyBudget && (
                  <div className="form-grid">
                    <Select
                      label="Monthly Budget"
                      value={hasMonthlyPool ? (value.monthly_pool?.pool_id || '') : ''}
                      onChange={handleMonthlyBudgetSelect}
                      options={monthlyBudgetSelectOptions}
                      hint="Pick an existing monthly budget or create a new one"
                    />
                  </div>
                )}

                {/* Create new monthly budget form */}
                {isCreatingMonthlyBudget && (
                  <div className="new-pool-card">
                    <h5 className="pool-card-title">Create a New Monthly Budget</h5>
                    <p className="pool-card-desc">
                      {value.limit_type === 'count'
                        ? 'Set the maximum number of redemptions per month. If you assign other rewards to this same budget later, they will all share it.'
                        : 'Set the maximum amount a user can spend per month. If you assign other rewards to this same budget later, they will all share it.'}
                    </p>
                    <div className="new-pool-fields">
                      <Input
                        label={value.limit_type === 'count' ? 'Monthly Limit (Count)' : 'Monthly Limit (USD)'}
                        type="number"
                        value={newMonthlyBudgetAmount}
                        onChange={setNewMonthlyBudgetAmount}
                        placeholder={value.limit_type === 'count' ? 'e.g., 2' : 'e.g., 100'}
                        hint={value.limit_type === 'count'
                          ? 'Maximum redemptions a user can make per month'
                          : 'Maximum dollars a user can spend per month'}
                      />
                    </div>
                    {newMonthlyBudgetAmount && parseFloat(newMonthlyBudgetAmount) > 0 && (
                      <div className="new-pool-preview">
                        {value.limit_type === 'count'
                          ? <>Users can redeem up to <strong>{parseInt(newMonthlyBudgetAmount, 10).toLocaleString()} times</strong> per month.</>
                          : <>Users can spend up to <strong>${parseFloat(newMonthlyBudgetAmount).toLocaleString()}</strong> per month.</>}
                      </div>
                    )}
                    <div className="pool-actions">
                      <button
                        type="button"
                        className="btn-create-pool"
                        onClick={handleCreateMonthlyBudget}
                        disabled={!newMonthlyBudgetAmount || parseFloat(newMonthlyBudgetAmount) <= 0}
                      >
                        Set Monthly Budget
                      </button>
                      <button
                        type="button"
                        className="btn-back-to-existing"
                        onClick={() => setIsCreatingMonthlyBudget(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Assigned monthly budget info card */}
                {hasMonthlyPool && !isCreatingMonthlyBudget && (
                  <div className="pool-info-card" style={{ marginTop: '1rem' }}>
                    <div className="pool-info-row">
                      <span className="pool-info-label">Monthly Limit</span>
                      <span className="pool-info-value">
                        {formatLimitDisplay(value.monthly_pool, currentLimitType)}/month
                      </span>
                    </div>
                    {(() => {
                      const matchingPool = availableMonthlyPools.find(
                        (p) => p.pool_id === value?.monthly_pool?.pool_id
                      );
                      if (matchingPool && matchingPool.members.length > 0) {
                        return (
                          <div className="pool-info-row">
                            <span className="pool-info-label">Shared with</span>
                            <span className="pool-info-value pool-members">
                              {matchingPool.members.map((m) => (
                                <span key={m.reward_id} className="pool-member-tag">
                                  {m.reward_key}/{m.reward_variant}
                                </span>
                              ))}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    <button
                      type="button"
                      className="btn-remove-pool"
                      onClick={handleRemoveMonthlyBudget}
                    >
                      Remove monthly budget
                    </button>
                  </div>
                )}
              </div>

              {/* Availability Section */}
              <div className="form-section">
                <h4>Availability</h4>
                <div className="checkbox-group">
                  <Checkbox
                    label="Available for redemption"
                    checked={value.available !== false}
                    onChange={(checked) => updateField('available', checked)}
                  />
                  <Checkbox
                    label="Show message on card"
                    checked={value.show_message_on_card === true}
                    onChange={(checked) => updateField('show_message_on_card', checked)}
                  />
                  <Checkbox
                    label="Collect pre-redemption data"
                    checked={value.collect_pre_redemption_data === true}
                    onChange={(checked) => updateField('collect_pre_redemption_data', checked)}
                  />
                </div>
                {(value.available === false || value.show_message_on_card) && (
                  <div style={{ marginTop: '1rem' }}>
                    <Input
                      label="Availability Message"
                      value={value.availability_message || ''}
                      onChange={(val) =>
                        updateField('availability_message', val || null)
                      }
                      placeholder="e.g., This reward is temporarily unavailable"
                      hint="Message shown when reward is unavailable or on card"
                    />
                  </div>
                )}
              </div>

              {/* Completion Points Section */}
              <div className="form-section">
                <h4>Completion Points</h4>
                <p className="section-hint">
                  Award bonus points when a fulfillment is marked COMPLETED.
                </p>
                <div className="form-grid">
                  <Input
                    label="Points to Award"
                    type="number"
                    value={value.completion_points?.toString() || ''}
                    onChange={(val) => {
                      const parsed = val ? parseInt(val, 10) : null;
                      updateField('completion_points', parsed && parsed > 0 ? parsed : null);
                    }}
                    placeholder="None"
                    hint="Must be greater than 0, or leave empty for none"
                  />
                </div>
              </div>

              {/* Clear Button */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-clear"
                  onClick={handleClear}
                >
                  Clear Configuration
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default RewardConfigEditor;
